import React, { useState, useEffect, useContext } from "react";
import { X } from "lucide-react";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";

const RESERVED_FOLDER_NAMES = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;

function defaultFolderName(keyName) {
  const cleaned = keyName.replace(/[^a-zA-Z0-9_ -]/g, "").trim();
  return RESERVED_FOLDER_NAMES.test(cleaned) ? "" : cleaned;
}

export default function CreateApiKeyModal({
  open,
  accessGroups,
  onClose,
  onCreated,
}) {
  const { createApiKey } = useContext(FieldConfigurationContext);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [attachmentFolder, setAttachmentFolder] = useState("");
  const [folderEdited, setFolderEdited] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [selectedGroupUuids, setSelectedGroupUuids] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const apiGroups = (accessGroups?.data || []).filter(
    (g) => g.group_type === "api"
  );

  useEffect(() => {
    if (!open) return;
    setName("");
    setEmail("");
    setAttachmentFolder("");
    setFolderEdited(false);
    setEnabled(true);
    setSelectedGroupUuids([]);
    setError(null);
  }, [open]);

  if (!open) return null;

  const toggleGroup = (uuid) => {
    setSelectedGroupUuids((prev) =>
      prev.includes(uuid) ? prev.filter((g) => g !== uuid) : [...prev, uuid]
    );
  };

  async function handleCreate() {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setError("Please enter a name for the API key.");
      return;
    }
    if (!trimmedEmail) {
      setError("Please enter an email address for verification.");
      return;
    }
    if (selectedGroupUuids.length === 0) {
      setError("Please select at least one Access Group.");
      return;
    }
    const trimmedFolder = attachmentFolder.trim();
    if (!trimmedFolder) {
      setError("Please enter an attachment folder.");
      return;
    }
    if (
      !/^[a-zA-Z0-9_ -]+$/.test(trimmedFolder) ||
      RESERVED_FOLDER_NAMES.test(trimmedFolder)
    ) {
      setError(
        "Attachment folder may only contain letters, numbers, spaces, underscores and hyphens, and cannot be a reserved name."
      );
      return;
    }

    setIsSaving(true);
    setError(null);

    const result = await createApiKey({
      name: trimmedName,
      email: trimmedEmail,
      attachment_folder: trimmedFolder,
      enabled,
      group_list: selectedGroupUuids,
    });

    setIsSaving(false);

    if (!result.success) {
      setError(result.error || "Failed to create API key. Please try again.");
      return;
    }

    onCreated(result.data);
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div
        className="jfm-container api-key-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="jfm-header">
          <span className="jfm-title">Create API Key</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <p style={{ fontSize: "13px", color: "#475569", margin: 0 }}>
            A verification email will be sent to the address below once the key
            is created.
          </p>
          <div className="api-key-form-field">
            <label htmlFor="api-key-name">Name</label>
            <input
              id="api-key-name"
              type="text"
              value={name}
              disabled={isSaving}
              onChange={(e) => {
                setName(e.target.value);
                if (!folderEdited) {
                  setAttachmentFolder(defaultFolderName(e.target.value));
                }
              }}
            />
          </div>
          <div className="api-key-form-field">
            <label htmlFor="api-key-attachment-folder">Attachment Folder</label>
            <input
              id="api-key-attachment-folder"
              type="text"
              maxLength={50}
              value={attachmentFolder}
              disabled={isSaving}
              onChange={(e) => {
                setAttachmentFolder(e.target.value);
                setFolderEdited(true);
              }}
            />
            <span style={{ fontSize: "12px", color: "#64748b" }}>
              Cannot be changed after the key is created.
            </span>
          </div>
          <div className="api-key-form-field">
            <label htmlFor="api-key-email">Email</label>
            <input
              id="api-key-email"
              type="email"
              value={email}
              disabled={isSaving}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="api-key-form-field">
            <label>Access Groups</label>
            <div className="api-key-group-list">
              {apiGroups.length === 0 && (
                <span style={{ color: "#64748b", fontSize: "13px" }}>
                  No access groups available.
                </span>
              )}
              {apiGroups.map((group) => (
                <label key={group.uuid} className="api-key-group-list__item">
                  <input
                    type="checkbox"
                    disabled={isSaving}
                    checked={selectedGroupUuids.includes(group.uuid)}
                    onChange={() => toggleGroup(group.uuid)}
                  />
                  {group.name}
                </label>
              ))}
            </div>
          </div>
          <div className="api-key-form-field api-key-form-field--checkbox">
            <label>
              <input
                type="checkbox"
                checked={enabled}
                disabled={isSaving}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Active
            </label>
          </div>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          <div />
          <div className="jfm-footer-actions">
            <button
              className="jfm-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="jfm-save"
              onClick={handleCreate}
              disabled={isSaving}
            >
              {isSaving ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
